import React, { useState, useEffect } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
import { Container, Row, Col, Card, Form, Button, Table, Modal, Alert, Spinner, Badge, Pagination } from 'react-bootstrap';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiFilter, FiSave, FiCheck, FiLock } from 'react-icons/fi';
import { userAPI, departmentAPI, roleAPI, permissionAPI, projectAPI } from '../utils/api';
import { normalizeAlphaNumLower } from '../utils/string';
import {
  FINANCE_DASHBOARD_PROJECTS,
  OM_DASHBOARD_PROJECTS,
  HR_DASHBOARD_PROJECTS,
  FINANCE_DUPLICATE_NAME_SET,
  OM_DUPLICATE_NAME_SET,
  HR_DUPLICATE_NAME_SET
} from '../utils/dashboardProjects';
import { isOmDepartmentKey, isHrDepartmentKey } from '../utils/departmentKeys';
import './UsersPage.css';

const EXCO_ALL_DASHBOARDS = '__ALL__';

const normalizeDepartmentKey = (value) => normalizeAlphaNumLower(value);

const normalizeProjectName = (value) => normalizeAlphaNumLower(value);

const ExcoDepartmentDashboards = ({
  deptId,
  departments,
  getDashboardsForDepartment,
  excoDashboardAccess,
  isExcoAllDashboardsSelected,
  toggleExcoAllDashboards,
  toggleExcoDashboard,
  submitting
}) => {
  const department = departments.find((item) => Number(item.id) === Number(deptId));
  if (!department) return null;

  const dashboards = getDashboardsForDepartment(department);

  return (
    <div className="mb-3 pb-3 border-bottom">
      <h6 className="text-primary mb-2">{department.name}</h6>
      {dashboards.length === 0 ? (
        <div className="text-muted small">No dashboards found for this department.</div>
      ) : (
        <>
          <Form.Check
            type="checkbox"
            id={`exco-dashboard-${deptId}-all`}
            label="Allow all dashboards in this department"
            checked={isExcoAllDashboardsSelected(deptId)}
            onChange={(e) => toggleExcoAllDashboards(deptId, e.target.checked)}
            disabled={submitting}
            className="mb-3"
          />
          <div className="d-flex flex-wrap gap-2">
            {dashboards.map((dashboard) => (
              <Form.Check
                key={`${deptId}-${dashboard.id}`}
                type="checkbox"
                id={`exco-dashboard-${deptId}-${dashboard.id}`}
                label={dashboard.name}
                checked={(excoDashboardAccess[deptId] || []).includes(dashboard.id)}
                onChange={(e) => toggleExcoDashboard(deptId, dashboard.id, e.target.checked)}
                disabled={submitting || isExcoAllDashboardsSelected(deptId)}
                className="me-3"
                inline
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const EditorDepartmentPermissions = ({
  deptId,
  departments,
  permissions,
  departmentPermissions,
  toggleDepartmentPermission,
  submitting
}) => {
  const dept = departments.find((item) => Number(item.id) === Number(deptId));
  if (!dept) return null;

  return (
    <div className="mb-3 pb-3 border-bottom">
      <h6 className="text-primary mb-2">{dept.name}</h6>
      <div className="d-flex flex-wrap gap-2">
        {permissions.map((perm) => (
          <Form.Check
            key={`${deptId}-${perm.id}`}
            type="checkbox"
            id={`perm-${deptId}-${perm.id}`}
            label={perm.description || perm.permissionName}
            checked={(departmentPermissions[deptId] || []).includes(perm.id)}
            onChange={(e) => toggleDepartmentPermission(deptId, perm.id, e.target.checked)}
            disabled={submitting}
            className="me-3"
            inline
          />
        ))}
      </div>
    </div>
  );
};

const UsersPage = () => {
  // States
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  // Form States
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
    departmentIds: [], // Changed to array for multi-select
    roleId: '',
    isActive: true
  });

  // Department-specific permissions: { deptId: [permissionIds] }
  const [departmentPermissions, setDepartmentPermissions] = useState({});
  const [excoDashboardAccess, setExcoDashboardAccess] = useState({});

  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [selectedUserPermissions, setSelectedUserPermissions] = useState([]);
  const [loadingUserPermissions, setLoadingUserPermissions] = useState(false);
  const [, setEditUserPermissions] = useState([]);
  const [loadingEditUserPermissions, setLoadingEditUserPermissions] = useState(false);
  const [editUserDirectPermissions, setEditUserDirectPermissions] = useState([]);

  const mapPermissionIdsToItems = (permissionIds) => {
    if (!Array.isArray(permissionIds)) return [];

    return permissionIds
      .map((permissionId) => {
        const match = permissions.find((permission) => Number(permission.id) === Number(permissionId));
        if (match) return match;

        return {
          id: `permission-${permissionId}`,
          description: `Permission #${permissionId}`
        };
      })
      .filter(Boolean);
  };

  const mapUserPermissionRowsToItems = (permissionRows) => {
    if (!Array.isArray(permissionRows)) return [];

    return permissionRows
      .map((row) => {
        const normalizedId = Number(row?.id || row?.permissionId);
        const knownPermission = permissions.find((permission) => Number(permission.id) === normalizedId);
        if (knownPermission) return knownPermission;

        return {
          id: Number.isFinite(normalizedId) ? normalizedId : `user-permission-${row?.permissionId}`,
          permissionName: row?.permissionName || null,
          description: row?.description || row?.permissionName || (row?.permissionId ? `Permission #${row.permissionId}` : 'Custom Permission'),
          module: row?.module || null,
          action: row?.action || null
        };
      })
      .filter(Boolean);
  };

  const mapPermissionRowsByDepartment = (permissionRows) => {
    if (!Array.isArray(permissionRows)) return {};

    return permissionRows.reduce((acc, row) => {
      const deptId = Number(row?.departmentId);
      const permissionId = Number(row?.permissionId || row?.id);
      if (!Number.isInteger(deptId) || deptId <= 0) return acc;
      if (!Number.isInteger(permissionId) || permissionId <= 0) return acc;

      const existing = Array.isArray(acc[deptId]) ? acc[deptId] : [];
      if (!existing.includes(permissionId)) {
        acc[deptId] = [...existing, permissionId];
      }
      return acc;
    }, {});
  };

  const getDashboardsForDepartment = (department) => {
    const deptId = Number(department?.id);
    if (!deptId) return [];

    const deptProjects = projects.filter((project) => Number(project.departmentId) === deptId);
    const normalizedDepartment = normalizeDepartmentKey(department?.name);

    if (normalizedDepartment === 'finance') {
      const financeRealProjects = deptProjects.filter(
        (project) => !FINANCE_DUPLICATE_NAME_SET.has(normalizeProjectName(project?.name))
      );

      return [
        ...FINANCE_DASHBOARD_PROJECTS.map((project) => ({ ...project, departmentId: deptId })),
        ...financeRealProjects
      ];
    }

    if (isOmDepartmentKey(normalizedDepartment)) {
      const omRealProjects = deptProjects.filter(
        (project) => !OM_DUPLICATE_NAME_SET.has(normalizeProjectName(project?.name))
      );

      return [
        ...OM_DASHBOARD_PROJECTS.map((project) => ({ ...project, departmentId: deptId })),
        ...omRealProjects
      ];
    }

    if (isHrDepartmentKey(normalizedDepartment)) {
      const hrRealProjects = deptProjects.filter(
        (project) => !HR_DUPLICATE_NAME_SET.has(normalizeProjectName(project?.name))
      );

      return [
        ...HR_DASHBOARD_PROJECTS.map((project) => ({ ...project, departmentId: deptId })),
        ...hrRealProjects
      ];
    }

    return deptProjects;
  };

  const toggleDepartmentSelection = (departmentId, checked, roleType) => {
    const normalizedDepartmentId = Number(departmentId);
    if (!Number.isInteger(normalizedDepartmentId) || normalizedDepartmentId <= 0) return;

    if (checked) {
      if (!selectedDepartmentIds.includes(normalizedDepartmentId)) {
        handleFormChange('departmentIds', [...selectedDepartmentIds, normalizedDepartmentId]);
      }

      if (roleType === 'EDITOR') {
        setDepartmentPermissions((prev) => ({
          ...prev,
          [normalizedDepartmentId]: Array.isArray(prev[normalizedDepartmentId]) ? prev[normalizedDepartmentId] : []
        }));
      }

      if (roleType === 'EXCO') {
        setExcoDashboardAccess((prev) => ({
          ...prev,
          [normalizedDepartmentId]: Array.isArray(prev[normalizedDepartmentId]) ? prev[normalizedDepartmentId] : []
        }));
      }

      return;
    }

    handleFormChange(
      'departmentIds',
      selectedDepartmentIds.filter((id) => Number(id) !== normalizedDepartmentId)
    );

    if (roleType === 'EDITOR') {
      setDepartmentPermissions((prev) => {
        const next = { ...prev };
        delete next[normalizedDepartmentId];
        return next;
      });
    }

    if (roleType === 'EXCO') {
      setExcoDashboardAccess((prev) => {
        const next = { ...prev };
        delete next[normalizedDepartmentId];
        return next;
      });
    }
  };

  const toggleExcoDashboard = (departmentId, dashboardId, checked) => {
    const normalizedDepartmentId = Number(departmentId);
    const normalizedDashboardId = String(dashboardId || '').trim();
    if (!Number.isInteger(normalizedDepartmentId) || normalizedDepartmentId <= 0 || !normalizedDashboardId) return;

    setExcoDashboardAccess((prev) => {
      const current = (Array.isArray(prev[normalizedDepartmentId]) ? prev[normalizedDepartmentId] : [])
        .filter((id) => String(id) !== EXCO_ALL_DASHBOARDS);
      return {
        ...prev,
        [normalizedDepartmentId]: checked
          ? [...new Set([...current, normalizedDashboardId])]
          : current.filter((id) => String(id) !== normalizedDashboardId)
      };
    });
  };

  const isExcoAllDashboardsSelected = (departmentId) => (
    (excoDashboardAccess[departmentId] || []).includes(EXCO_ALL_DASHBOARDS)
  );

  const toggleExcoAllDashboards = (departmentId, checked) => {
    const normalizedDepartmentId = Number(departmentId);
    if (!Number.isInteger(normalizedDepartmentId) || normalizedDepartmentId <= 0) return;

    setExcoDashboardAccess((prev) => ({
      ...prev,
      [normalizedDepartmentId]: checked ? [EXCO_ALL_DASHBOARDS] : []
    }));
  };

  const toggleDepartmentPermission = (departmentId, permissionId, checked) => {
    const normalizedDepartmentId = Number(departmentId);
    const normalizedPermissionId = Number(permissionId);
    if (!Number.isFinite(normalizedDepartmentId) || !Number.isFinite(normalizedPermissionId)) return;

    setDepartmentPermissions((prev) => {
      const currentPerms = Array.isArray(prev[normalizedDepartmentId]) ? prev[normalizedDepartmentId] : [];
      const nextPerms = checked
        ? [...new Set([...currentPerms, normalizedPermissionId])]
        : currentPerms.filter((id) => Number(id) !== normalizedPermissionId);

      return {
        ...prev,
        [normalizedDepartmentId]: nextPerms
      };
    });
  };

  const renderExcoDepartmentAccess = () => (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Departments <span className="text-danger">*</span></Form.Label>
        <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {departments.map((dept) => (
            <Form.Check
              key={`exco-department-${dept.id}`}
              type="checkbox"
              id={`exco-department-${dept.id}`}
              label={dept.name}
              checked={selectedDepartmentIds.includes(dept.id)}
              onChange={(e) => toggleDepartmentSelection(dept.id, e.target.checked, 'EXCO')}
              disabled={submitting}
              className="mb-2"
            />
          ))}
        </div>
        <Form.Text className="text-muted">
          Select the departments this EXCO user can access.
        </Form.Text>
      </Form.Group>

	      {selectedDepartmentIds.length > 0 && (
	        <Form.Group className="mb-3">
	          <Form.Label>Dashboard Access <span className="text-danger">*</span></Form.Label>
	          <div className="border rounded p-3">
	            {selectedDepartmentIds.map((deptId) => (
	              <ExcoDepartmentDashboards
	                key={`exco-dashboards-${deptId}`}
	                deptId={deptId}
	                departments={departments}
	                getDashboardsForDepartment={getDashboardsForDepartment}
	                excoDashboardAccess={excoDashboardAccess}
	                isExcoAllDashboardsSelected={isExcoAllDashboardsSelected}
	                toggleExcoAllDashboards={toggleExcoAllDashboards}
	                toggleExcoDashboard={toggleExcoDashboard}
	                submitting={submitting}
	              />
	            ))}
	          </div>
	          <Form.Text className="text-muted">
	            Choose either all dashboards for a department or only the selected dashboards and sub-dashboards.
	          </Form.Text>
	        </Form.Group>
      )}
    </>
  );

  const renderEditorDepartmentPermissions = () => (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Departments <span className="text-danger">*</span></Form.Label>
        <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {departments.map((dept) => (
            <Form.Check
              key={`editor-dept-${dept.id}`}
              type="checkbox"
              id={`editor-dept-${dept.id}`}
              label={dept.name}
              checked={selectedDepartmentIds.includes(dept.id)}
              onChange={(e) => toggleDepartmentSelection(dept.id, e.target.checked, 'EDITOR')}
              disabled={submitting}
              className="mb-2"
            />
          ))}
        </div>
        <Form.Text className="text-muted">
          Select one or more departments for this EDITOR user.
        </Form.Text>
      </Form.Group>

	      {selectedDepartmentIds.length > 0 && (
	        <Form.Group className="mb-3">
	          <Form.Label>Department Permissions <span className="text-danger">*</span></Form.Label>
	          <div className="border rounded p-3">
	            {selectedDepartmentIds.map((deptId) => (
	              <EditorDepartmentPermissions
	                key={`editor-perms-${deptId}`}
	                deptId={deptId}
	                departments={departments}
	                permissions={permissions}
	                departmentPermissions={departmentPermissions}
	                toggleDepartmentPermission={toggleDepartmentPermission}
	                submitting={submitting}
	              />
	            ))}
	          </div>
	          <Form.Text className="text-muted">
	            Select specific permissions for each department.
	          </Form.Text>
	        </Form.Group>
      )}
    </>
  );

  const mergePermissions = (...permissionGroups) => {
    const merged = [];
    const seen = new Set();

    permissionGroups.flat().forEach((permission) => {
      if (!permission) return;
      const key = Number.isFinite(Number(permission.id))
        ? `id-${Number(permission.id)}`
        : `id-${String(permission.id)}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(permission);
    });

    return merged;
  };

  const fetchRolePermissions = async (roleId) => {
    const normalizedRoleId = Number(roleId);
    if (!Number.isFinite(normalizedRoleId)) return [];

    const response = await roleAPI.getRolePermissions(normalizedRoleId);
    const permissionIds = Array.isArray(response?.data?.permissionIds) ? response.data.permissionIds : [];
    return mapPermissionIdsToItems(permissionIds);
  };

  const fetchUserPermissionDetails = async (userId) => {
    const normalizedUserId = Number(userId);
    if (!Number.isFinite(normalizedUserId)) {
      return { rows: [], items: [], byDepartment: {}, departmentIds: [], excoDashboardAccess: {} };
    }

    const response = await userAPI.getUserById(normalizedUserId);
    const permissionRows = Array.isArray(response?.data?.departmentPermissions) ? response.data.departmentPermissions : [];
    const departmentIds = Array.isArray(response?.data?.departmentIds) ? response.data.departmentIds : [];
    const excoDashboardAccess = response?.data?.excoDashboardAccess || {};

    return {
      rows: permissionRows,
      items: mapUserPermissionRowsToItems(permissionRows),
      byDepartment: mapPermissionRowsByDepartment(permissionRows),
      departmentIds: departmentIds
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value > 0),
      excoDashboardAccess
    };
  };

  const fetchUserDirectPermissions = async (userId) => {
    const details = await fetchUserPermissionDetails(userId);
    return details.items;
  };

  const loadViewUserPermissions = async (user) => {
    try {
      setLoadingUserPermissions(true);
      const [rolePermissions, userDirectPermissions] = await Promise.all([
        fetchRolePermissions(user?.roleId),
        fetchUserDirectPermissions(user?.id)
      ]);
      setSelectedUserPermissions(mergePermissions(rolePermissions, userDirectPermissions));
    } catch (err) {
      console.error('Failed to load user permissions:', err);
      setSelectedUserPermissions([]);
    } finally {
      setLoadingUserPermissions(false);
    }
  };

  const loadEditUserPermissions = async (roleId, userId, existingDirectPermissions = null) => {
    try {
      setLoadingEditUserPermissions(true);
      const rolePermissions = await fetchRolePermissions(roleId);
      const userDirectPermissions = Array.isArray(existingDirectPermissions)
        ? existingDirectPermissions
        : await fetchUserDirectPermissions(userId);

      if (!Array.isArray(existingDirectPermissions)) {
        setEditUserDirectPermissions(userDirectPermissions);
      }

      setEditUserPermissions(mergePermissions(rolePermissions, userDirectPermissions));
    } catch (err) {
      console.error('Failed to load edit role permissions:', err);
      setEditUserPermissions([]);
    } finally {
      setLoadingEditUserPermissions(false);
    }
  };

  /**
   * Fetch initial data
   */
  useEffect(() => {
    fetchInitialData();
  }, []);

  /**
   * Apply filters when data changes
   */
  useEffect(() => {
    applyFilters(users, selectedDept, selectedRole, selectedStatus, searchTerm);
  }, [users]);

  /**
   * Fetch users, departments, and roles
   */
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch users
      const usersResponse = await userAPI.getAllUsers();
      if (usersResponse.success && usersResponse.data) {
        setUsers(usersResponse.data);
        applyFilters(usersResponse.data, selectedDept, selectedRole, selectedStatus, searchTerm);
      }

      // Fetch departments
      const deptsResponse = await departmentAPI.getAllDepartments();
      if (deptsResponse.success && deptsResponse.data) {
        setDepartments(deptsResponse.data);
      }

      // Fetch roles
      const rolesResponse = await roleAPI.getAllRoles();
      if (rolesResponse.success && rolesResponse.data) {
        setRoles(rolesResponse.data);
      }

      // Fetch projects
      const projectsResponse = await projectAPI.getAllProjects();
      if (projectsResponse.success && projectsResponse.data) {
        setProjects(Array.isArray(projectsResponse.data) ? projectsResponse.data : []);
      }

      // Fetch permissions
      const permsResponse = await permissionAPI.getAll();
      if (permsResponse.success && permsResponse.data) {
        setPermissions(permsResponse.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Apply all filters
   */
  const applyFilters = (allUsers, deptId, roleId, status, search) => {
    let filtered = allUsers;

    // Filter by department
    if (deptId) {
      const departmentId = Number(deptId);
      filtered = filtered.filter((user) => {
        const departmentIds = Array.isArray(user.departmentIds)
          ? user.departmentIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
          : [];
        if (departmentIds.includes(departmentId)) return true;
        return Number(user.departmentId) === departmentId;
      });
    }

    // Filter by role
    if (roleId) {
      filtered = filtered.filter(u => u.roleId === roleId);
    }

    // Filter by status
    if (status && status !== 'all') {
      const isActive = status === 'active';
      filtered = filtered.filter(u => u.isActive === isActive);
    }

    // Filter by search term
    if (search) {
      const tokens = String(search)
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);

      const buildHaystack = (user) => {
        const deptNames = getDepartmentNames(user);
        const roleName = getRoleName(user?.roleId);
        return [
          user?.id,
          user?.employeeId,
          user?.firstName,
          user?.lastName,
          `${user?.firstName || ''} ${user?.lastName || ''}`,
          user?.email,
          user?.phoneNumber,
          deptNames,
          roleName
        ]
          .map((v) => (v === null || v === undefined ? '' : String(v)))
          .join(' ')
          .toLowerCase();
      };

      filtered = filtered.filter((user) => {
        const haystack = buildHaystack(user);
        return tokens.every((token) => haystack.includes(token));
      });
    }

    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page
  };

  const clampPage = (page, pagesTotal) => {
    const total = Number(pagesTotal) || 1;
    const next = Number(page) || 1;
    return Math.min(Math.max(next, 1), Math.max(total, 1));
  };

  const buildPaginationItems = (pagesTotal, page, windowSize = 2) => {
    const total = Number(pagesTotal) || 1;
    const current = clampPage(page, total);
    if (total <= 7) return Array.from({ length: total }, (_, idx) => idx + 1);

    const firstPage = 1;
    const lastPage = total;
    const start = Math.max(2, current - windowSize);
    const end = Math.min(total - 1, current + windowSize);

    const items = [firstPage];
    if (start > 2) items.push('ellipsis-left');
    for (let p = start; p <= end; p += 1) items.push(p);
    if (end < total - 1) items.push('ellipsis-right');
    items.push(lastPage);
    return items;
  };

  /**
   * Handle department change
   */
  const handleDeptChange = (deptId) => {
    setSelectedDept(deptId);
    applyFilters(users, deptId, selectedRole, selectedStatus, searchTerm);
  };

  /**
   * Handle role change
   */
  const handleRoleChange = (roleId) => {
    setSelectedRole(roleId);
    applyFilters(users, selectedDept, roleId, selectedStatus, searchTerm);
  };

  /**
   * Handle status change
   */
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    applyFilters(users, selectedDept, selectedRole, status, searchTerm);
  };

  /**
   * Handle search
   */
  const handleSearch = (term) => {
    setSearchTerm(term);
    applyFilters(users, selectedDept, selectedRole, selectedStatus, term);
  };

  /**
   * Handle form input change
   */
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'roleId' && showEditModal) {
      loadEditUserPermissions(value, selectedUser?.id, editUserDirectPermissions);
    }
  };

  const toggleEditDepartmentPermissionSelection = (permissionId, checked) => {
    if (!activeEditDepartmentId) return;
    setDepartmentPermissions((prev) => {
      const current = Array.isArray(prev[activeEditDepartmentId]) ? prev[activeEditDepartmentId] : [];
      const next = checked
        ? [...new Set([...current, permissionId])]
        : current.filter((id) => id !== permissionId);
      return {
        ...prev,
        [activeEditDepartmentId]: next
      };
    });
  };

  const renderRoleSpecificEditFields = () => {
    const currentRole = roles.find((role) => role.id === formData.roleId);
    const roleType = currentRole?.roleType;

    if (roleType === 'ADMIN') {
      return (
        <Form.Group className="mb-3">
          <Form.Label>Departments & Projects</Form.Label>
          <div className="alert alert-info mb-0">
            <i className="bi bi-info-circle me-2"></i>
            This user will automatically have access to <strong>ALL departments and projects</strong>
          </div>
        </Form.Group>
      );
    }

    if (roleType === 'EXCO') return renderExcoDepartmentAccess();

    if (roleType === 'EDITOR') {
      return (
        <>
          {renderEditorDepartmentPermissions()}
          <Form.Group className="mb-3">
            <Form.Label>Permissions</Form.Label>
            {loadingEditUserPermissions ? (
              <div className="text-muted small">Loading permissions...</div>
            ) : !activeEditDepartmentId ? (
              <div className="text-muted small">Select a department to edit permissions.</div>
            ) : permissions.length > 0 ? (
              <div className="edit-permissions-grid">
                {permissions.map((permission) => (
                  <Form.Check
                    key={permission.id}
                    type="checkbox"
                    id={`edit-dept-${activeEditDepartmentId}-perm-${permission.id}`}
                    className="edit-permission-item"
                    label={permission.description || permission.permissionName || permission.name || `${permission.module}:${permission.action}`}
                    checked={editableDepartmentPermissionIds.includes(permission.id)}
                    onChange={(e) => toggleEditDepartmentPermissionSelection(permission.id, e.target.checked)}
                    disabled={submitting}
                  />
                ))}
              </div>
            ) : (
              <div className="text-muted small">No permissions available.</div>
            )}
          </Form.Group>
        </>
      );
    }

    return null;
  };

  /**
   * Reset form
   */
  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      employeeId: '',
      password: '',
      phoneNumber: '',
      departmentIds: [], // Changed to array
      roleId: '',
      isActive: true
    });
    setDepartmentPermissions({});
    setExcoDashboardAccess({});
    setEditUserPermissions([]);
    setEditUserDirectPermissions([]);
    setLoadingEditUserPermissions(false);
  };

  /**
   * Open create modal
   */
  const handleOpenCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  /**
   * Open edit modal
   */
  const handleOpenEditModal = async (user) => {
    setSelectedUser(user);
    setEditUserPermissions([]);
    setEditUserDirectPermissions([]);
    setShowEditModal(true);
    const normalizedDepartmentIds = Array.isArray(user.departmentIds)
      ? user.departmentIds
      : (user.departmentId ? [user.departmentId] : []);

    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      employeeId: user.employeeId || '',
      phoneNumber: user.phoneNumber || '',
      departmentId: user.departmentId || normalizedDepartmentIds[0] || '',
      departmentIds: normalizedDepartmentIds,
      roleId: user.roleId,
      isActive: user.isActive
    });
    try {
      const details = await fetchUserPermissionDetails(user.id);
      setDepartmentPermissions(details.byDepartment);
      setExcoDashboardAccess(details.excoDashboardAccess || {});
      setFormData((prev) => ({
        ...prev,
        departmentIds: details.departmentIds.length ? details.departmentIds : prev.departmentIds,
        departmentId: prev.departmentId || details.departmentIds[0] || ''
      }));
      setEditUserDirectPermissions(details.items);
      await loadEditUserPermissions(user.roleId, user.id, details.items);
    } catch (err) {
      console.error('Failed to load user direct permissions:', err);
      setDepartmentPermissions({});
      setEditUserDirectPermissions([]);
      await loadEditUserPermissions(user.roleId, user.id, []);
    }
  };

  /**
   * Open view modal
   */
  const handleOpenViewModal = async (user) => {
    setSelectedUser(user);
    setSelectedUserPermissions([]);
    setShowViewModal(true);
    await loadViewUserPermissions(user);
  };

  /**
   * Open delete confirmation modal
   */
  const handleOpenDeleteModal = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  /**
   * Validate form
   */
	  const validateRequiredFields = (data) => {
	    if (!data.firstName.trim()) return 'First name is required';
	    if (!data.lastName.trim()) return 'Last name is required';
	    if (!data.email.trim()) return 'Email is required';
	    return null;
	  };

	  const isValidEmailSimple = (rawEmail) => {
	    const email = String(rawEmail || '').trim();
	    if (!email) return false;
	    if (email.length > 254) return false;
	    if (/\s/.test(email)) return false;

	    const atIndex = email.indexOf('@');
	    if (atIndex <= 0) return false;
	    if (atIndex !== email.lastIndexOf('@')) return false;

	    const local = email.slice(0, atIndex);
	    const domain = email.slice(atIndex + 1);
	    if (local.length === 0 || domain.length === 0) return false;
	    if (local.length > 64) return false;
	    if (!domain.includes('.')) return false;
	    if (domain.startsWith('.') || domain.endsWith('.')) return false;
	    if (domain.includes('..')) return false;

	    return true;
	  };

	  const validateEmail = (email) => (
	    isValidEmailSimple(email) ? null : 'Please enter a valid email address'
	  );

  const validatePassword = (data) => {
    if (selectedUser) return null;
    if (!data.password) return 'Password is required';
    if (data.password.length < 8) return 'Password must be at least 8 characters';
    if (data.password.length > 128) return 'Password cannot exceed 128 characters';
    return null;
  };

  const validateRoleSelection = (data) => (!data.roleId ? 'Role is required' : null);

  const validateEditorDepartments = (role) => {
    if (!role || role.roleType !== 'EDITOR' || selectedUser) return null;
    if (!selectedDepartmentIds.length) return 'EDITOR users must be assigned to at least one department';

    for (const deptId of selectedDepartmentIds) {
      const perms = departmentPermissions[deptId] || [];
      if (perms.length > 0) continue;
      const dept = departments.find((d) => d.id === deptId);
      return `Please select at least one permission for ${dept ? dept.name : 'selected department'}`;
    }

    return null;
  };

  const validateExcoDepartments = (role) => {
    if (!role || role.roleType !== 'EXCO') return null;
    if (!selectedDepartmentIds.length) return 'EXCO users must be assigned to at least one department';
    return null;
  };

  const validateForm = () => {
    const selectedRole = roles.find((r) => r.id === formData.roleId);
    const validators = [
      () => validateRequiredFields(formData),
      () => validateEmail(formData.email),
      () => validatePassword(formData),
      () => validateRoleSelection(formData),
      () => validateEditorDepartments(selectedRole),
      () => validateExcoDepartments(selectedRole)
    ];

    for (const validator of validators) {
      const message = validator();
      if (!message) continue;
      setError(message);
      return false;
    }

    return true;
  };

  /**
   * Handle create user
   */
  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      // Prepare request data
      const requestData = {
        ...formData,
        departmentPermissions,
        excoDashboardAccess
      };

      const response = await userAPI.createUser(requestData);

      if (response.success && response.data) {
        setSuccess('User created successfully!');
        setUsers([...users, response.data]);
        applyFilters([...users, response.data], selectedDept, selectedRole, selectedStatus, searchTerm);
        setShowCreateModal(false);
        resetForm();
      }
    } catch (err) {
      console.error('Create error:', err);
      setError(err.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle update user
   */
  const handleUpdateUser = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const requestData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        employeeId: formData.employeeId,
        phoneNumber: formData.phoneNumber,
        isActive: formData.isActive,
        roleId: formData.roleId,
        departmentIds: selectedDepartmentIds,
        departmentPermissions,
        excoDashboardAccess
      };

      const response = await userAPI.updateUser(selectedUser.id, requestData);

      if (response.success && response.data) {
        setSuccess('User updated successfully!');
        setUsers(users.map(u => u.id === selectedUser.id ? response.data : u));
        applyFilters(
          users.map(u => u.id === selectedUser.id ? response.data : u),
          selectedDept,
          selectedRole,
          selectedStatus,
          searchTerm
        );
        setShowEditModal(false);
        resetForm();
      }
    } catch (err) {
      console.error('Update error:', err);
      setError(err.message || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle delete user
   */
  const handleDeleteUser = async () => {
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      await userAPI.deleteUser(userToDelete.id);

      setSuccess('User deleted successfully!');
      setUsers(users.filter(u => u.id !== userToDelete.id));
      applyFilters(
        users.filter(u => u.id !== userToDelete.id),
        selectedDept,
        selectedRole,
        selectedStatus,
        searchTerm
      );
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message || 'Failed to delete user');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Get department name
   */
  const getDepartmentNames = (user) => {
    if (!user) return 'N/A';

    const idsFromArray = Array.isArray(user.departmentIds)
      ? user.departmentIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
      : [];

    const fallbackId = Number(user.departmentId);
    const uniqueDeptIds = Array.from(new Set(
      idsFromArray.length
        ? idsFromArray
        : (Number.isFinite(fallbackId) ? [fallbackId] : [])
    ));

    if (!uniqueDeptIds.length) return 'N/A';

    const names = uniqueDeptIds
      .map((deptId) => departments.find((dept) => Number(dept.id) === deptId)?.name)
      .filter(Boolean);

    return names.length ? names.join(', ') : 'N/A';
  };

  /**
   * Get role name
   */
  const getRoleName = (roleId) => {
    const role = roles.find((r) => Number(r.id) === Number(roleId));
    return role?.roleName || role?.name || role?.roleType || 'N/A';
  };

  /**
   * Pagination
   */
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));
  const currentPageSafe = clampPage(currentPage, totalPages);
  const startIndex = (currentPageSafe - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  const endIndex = Math.min(startIndex + itemsPerPage, filteredUsers.length);
  const paginationItems = buildPaginationItems(totalPages, currentPageSafe);
  const selectedDepartmentIds = Array.isArray(formData?.departmentIds) ? formData.departmentIds : [];
  const activeEditDepartmentId = Number(formData?.departmentId) || Number(selectedDepartmentIds?.[0]) || null;
  const editableDepartmentPermissionIds = activeEditDepartmentId ? (departmentPermissions[activeEditDepartmentId] || []) : [];

  if (loading) {
    return (
      <Container fluid className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading users...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="users-page py-4">
      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <div className="users-header mb-4">
        <div>
          <h1 className="mb-2">User Management</h1>
          <p className="text-muted">Create, manage, and organize user accounts</p>
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={handleOpenCreateModal}
          className="create-btn"
        >
          <FiPlus className="me-2" />
          Add New User
        </Button>
      </div>

      {/* Filters Section */}
      <Card className="filters-card shadow-sm mb-4">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">
            <FiFilter className="me-2" />
            Filters
          </h5>
        </Card.Header>

        <Card.Body>
          <Row className="g-3">
            {/* Department Filter */}
            <Col md={2}>
              <Form.Group>
                <Form.Label className="filter-label">Department</Form.Label>
                <Form.Select
                  value={selectedDept || ''}
                  onChange={(e) => handleDeptChange(e.target.value ? parseInt(e.target.value) : null)}
                  className="filter-select"
                >
                  <option value="">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Role Filter */}
            <Col md={2}>
              <Form.Group>
                <Form.Label className="filter-label">Role</Form.Label>
                <Form.Select
                  value={selectedRole || ''}
                  onChange={(e) => handleRoleChange(e.target.value ? parseInt(e.target.value) : null)}
                  className="filter-select"
                >
                  <option value="">All Roles</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.roleName || role.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Status Filter */}
            <Col md={2}>
              <Form.Group>
                <Form.Label className="filter-label">Status</Form.Label>
                <Form.Select
                  value={selectedStatus}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Search */}
            <Col md={6}>
              <Form.Group>
                <Form.Label className="filter-label">Search</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="filter-input"
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Users Table */}
      <Card className="users-card shadow-sm">
        <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            Users
            <Badge bg="primary" className="ms-2">
              {filteredUsers.length}
            </Badge>
          </h5>
        </Card.Header>

        <Card.Body className="p-0">
          {filteredUsers.length === 0 ? (
            <div className="no-users text-center py-5">
              <div className="no-users-icon">👥</div>
              <p className="text-muted mt-3 mb-2">
                {users.length === 0 ? 'No users created yet' : 'No users match your filters'}
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={handleOpenCreateModal}
              >
                <FiPlus className="me-1" />
                Create First User
              </Button>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Employee ID</th>
                      <th>Phone</th>
                      <th>Department</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map(user => (
                      <tr key={user.id} className="user-row">
                        <td>
                          <div className="user-name-cell">
                            <div className="user-avatar">
                              {user.firstName.charAt(0).toUpperCase()}
                              {user.lastName.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="user-name">
                                {user.firstName} {user.lastName}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <small>{user.email}</small>
                        </td>
                        <td>
                          <small>{user.employeeId || '-'}</small>
                        </td>
                        <td>
                          <small>{user.phoneNumber || '-'}</small>
                        </td>
                        <td>
                          <Badge bg="light" text="dark" className="dept-badge">
                            {getDepartmentNames(user)}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg="info">
                            {getRoleName(user.roleId)}
                          </Badge>
                        </td>
                        <td>
                          <StatusBadge isActive={user.isActive} />
                        </td>
                        <td>
                          <small>{new Date(user.createdAt).toLocaleDateString()}</small>
                        </td>
                        <td>
                          <div className="user-actions">
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleOpenViewModal(user)}
                              title="View"
                              className="action-btn"
                            >
                              <FiEye size={16} />
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleOpenEditModal(user)}
                              title="Edit"
                              className="action-btn"
                            >
                              <FiEdit2 size={16} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleOpenDeleteModal(user)}
                              title="Delete"
                              className="action-btn"
                            >
                              <FiTrash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-wrapper d-flex flex-column flex-md-row align-items-center justify-content-between gap-2">
                  <div className="text-muted small">
                    Showing {startIndex + 1}-{endIndex} of {filteredUsers.length}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Select
                      size="sm"
                      value={itemsPerPage}
                      onChange={(e) => {
                        const nextSize = Number(e.target.value) || 25;
                        setItemsPerPage(nextSize);
                        setCurrentPage(1);
                      }}
                      style={{ width: 120 }}
                      aria-label="Items per page"
                    >
                      <option value={10}>10 / page</option>
                      <option value={25}>25 / page</option>
                      <option value={50}>50 / page</option>
                      <option value={100}>100 / page</option>
                    </Form.Select>

                    <Pagination className="mb-0">
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPageSafe === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage(currentPageSafe - 1)}
                      disabled={currentPageSafe === 1}
                    />

                    {paginationItems.map((item) => {
                      if (typeof item === 'string' && item.startsWith('ellipsis')) {
                        return <Pagination.Ellipsis key={item} disabled />;
                      }
                      const pageNum = Number(item);
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === currentPageSafe}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}

                    <Pagination.Next
                      onClick={() => setCurrentPage(currentPageSafe + 1)}
                      disabled={currentPageSafe === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPageSafe === totalPages}
                    />
                  </Pagination>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiPlus className="me-2" />
            Create New User
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleCreateUser}>
            <Row>
              {/* First Name */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter first name"
                    value={formData.firstName}
                    onChange={(e) => handleFormChange('firstName', e.target.value)}
                    disabled={submitting}
                    className="form-input"
                  />
                </Form.Group>
              </Col>

              {/* Last Name */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter last name"
                    value={formData.lastName}
                    onChange={(e) => handleFormChange('lastName', e.target.value)}
                    disabled={submitting}
                    className="form-input"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Email */}
            <Form.Group className="mb-3">
              <Form.Label>Email <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            {/* Employee ID */}
            <Form.Group className="mb-3">
              <Form.Label>Employee ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter employee ID"
                value={formData.employeeId}
                onChange={(e) => handleFormChange('employeeId', e.target.value)}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            {/* Password */}
            <Form.Group className="mb-3">
              <Form.Label>Password <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="password"
                autoComplete="off"
                maxLength={128}
                placeholder="Enter password (min 8 characters)"
                value={formData.password}
                onChange={(e) => handleFormChange('password', e.target.value)}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            {/* Phone */}
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter phone number"
                value={formData.phoneNumber}
                onChange={(e) => handleFormChange('phoneNumber', e.target.value)}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            {/* Role */}
            <Form.Group className="mb-3">
              <Form.Label>Role <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={formData.roleId}
                onChange={(e) => handleFormChange('roleId', e.target.value ? parseInt(e.target.value) : '')}
                disabled={submitting}
                className="form-input"
              >
                <option value="">Select Role</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.roleName || role.name} - {role.description}
                  </option>
                ))}
              </Form.Select>
              {formData.roleId && (() => {
                const selectedRole = roles.find(r => r.id === formData.roleId);
                if (selectedRole) {
                  return (
                    <Form.Text className="text-muted d-block mt-2">
                      <strong>Permissions:</strong>{' '}
                      {selectedRole.roleType === 'ADMIN' && '✓ Full access to all departments, projects, and system settings'}
                      {selectedRole.roleType === 'EXCO' && '✓ View-only access to the departments and dashboards assigned by admin'}
                      {selectedRole.roleType === 'EDITOR' && '✓ Can create, edit, and manage assigned department/project data'}
                      {selectedRole.roleType === 'IT_HEAD' && '✓ Approves IT Asset changes and can manage assigned departments/projects'}
                    </Form.Text>
                  );
                }
              })()}
            </Form.Group>

            {/* Department Access */}
            {(() => {
              const currentRole = roles.find((role) => role.id === formData.roleId);
              const roleType = currentRole?.roleType;

              if (roleType === 'ADMIN') {
                return (
                  <Form.Group className="mb-3">
                    <Form.Label>Departments & Projects</Form.Label>
                    <div className="alert alert-info mb-0">
                      <i className="bi bi-info-circle me-2"></i>
                      This user will automatically have access to <strong>ALL departments and projects</strong>
                    </div>
                  </Form.Group>
                );
              }

              if (roleType === 'EXCO') {
                return renderExcoDepartmentAccess();
              }

              if (roleType === 'EDITOR') {
                return renderEditorDepartmentPermissions();
              }

              return null;
            })()}

            {/* Active Status */}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="User is Active"
                checked={formData.isActive}
                onChange={(e) => handleFormChange('isActive', e.target.checked)}
                disabled={submitting}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreateModal(false);
              resetForm();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateUser}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <FiPlus className="me-2" />
                Create User
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="xl" dialogClassName="users-fixed-modal">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiEdit2 className="me-2" />
            Edit User
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleUpdateUser} className="compact-user-form">
            <Row className="g-2">
              {/* First Name */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter first name"
                    value={formData.firstName}
                    onChange={(e) => handleFormChange('firstName', e.target.value)}
                    disabled={submitting}
                    className="form-input"
                  />
                </Form.Group>
              </Col>

              {/* Last Name */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter last name"
                    value={formData.lastName}
                    onChange={(e) => handleFormChange('lastName', e.target.value)}
                    disabled={submitting}
                    className="form-input"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Email */}
            <Form.Group className="mb-3">
              <Form.Label>Email <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            {/* Employee ID */}
            <Form.Group className="mb-3">
              <Form.Label>Employee ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter employee ID"
                value={formData.employeeId}
                onChange={(e) => handleFormChange('employeeId', e.target.value)}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            {/* Phone */}
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter phone number"
                value={formData.phoneNumber}
                onChange={(e) => handleFormChange('phoneNumber', e.target.value)}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            <Row className="g-2">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.roleId}
                    onChange={(e) => handleFormChange('roleId', e.target.value ? parseInt(e.target.value, 10) : '')}
                    disabled={submitting}
                    className="form-input"
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.roleName || role.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {renderRoleSpecificEditFields()}

            {/* Active Status */}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="User is Active"
                checked={formData.isActive}
                onChange={(e) => handleFormChange('isActive', e.target.checked)}
                disabled={submitting}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditModal(false);
              resetForm();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateUser}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <FiSave className="me-2" />
                Update User
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="xl" dialogClassName="users-fixed-modal">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiEye className="me-2" />
            User Details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedUser && (
            <div className="user-details">
              <div className="user-avatar-large">
                {selectedUser.firstName.charAt(0).toUpperCase()}
                {selectedUser.lastName.charAt(0).toUpperCase()}
              </div>

              <div className="detail-group">
                <label>Full Name</label>
                <p>{selectedUser.firstName} {selectedUser.lastName}</p>
              </div>

              <div className="detail-group">
                <label>Email</label>
                <p>{selectedUser.email}</p>
              </div>

              <div className="detail-group">
                <label>Phone</label>
                <p>{selectedUser.phoneNumber || 'Not provided'}</p>
              </div>

              <div className="detail-group">
                <label>Department</label>
                <p>{getDepartmentNames(selectedUser)}</p>
              </div>

              <div className="detail-group">
                <label>Role</label>
                <p>
                  <Badge bg="info">
                    {getRoleName(selectedUser.roleId)}
                  </Badge>
                </p>
              </div>

              <div className="detail-group">
                <label>Status</label>
                <p>
                  <StatusBadge isActive={selectedUser.isActive} />
                </p>
              </div>

              <div className="detail-group">
                <label>Permissions</label>
                {loadingUserPermissions ? (
                  <div className="text-muted small">Loading permissions...</div>
                ) : selectedUserPermissions.length > 0 ? (
                  <div className="user-permissions-list">
                    {selectedUserPermissions.map((permission) => (
                      <Badge key={permission.id} bg="light" text="dark" className="user-permission-badge">
                        {permission.description || permission.permissionName || permission.name || `${permission.module}:${permission.action}`}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p>No permissions assigned</p>
                )}
              </div>

              <div className="detail-group">
                <label>Member Since</label>
                <p>{new Date(selectedUser.createdAt).toLocaleString()}</p>
              </div>

              {selectedUser.updatedAt && (
                <div className="detail-group">
                  <label>Last Updated</label>
                  <p>{new Date(selectedUser.updatedAt).toLocaleString()}</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowViewModal(false);
              handleOpenEditModal(selectedUser);
            }}
          >
            <FiEdit2 className="me-2" />
            Edit
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {userToDelete && (
            <div className="delete-confirmation">
              <div className="warning-icon">⚠️</div>
              <p className="mt-3">
                Are you sure you want to delete <strong>{userToDelete.firstName} {userToDelete.lastName}</strong>?
              </p>
              <p className="text-muted small">
                This action cannot be undone. The user account will be permanently removed.
              </p>
              <div className="deletion-info">
                <small>Email: {userToDelete.email}</small>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setUserToDelete(null);
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteUser}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <FiTrash2 className="me-2" />
                Delete User
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

/**
 * Status Badge Component
 */
const StatusBadge = ({ isActive }) => {
  return (
    <Badge bg={isActive ? 'success' : 'secondary'}>
      {isActive ? (
        <>
          <FiCheck size={12} className="me-1" />
          Active
        </>
      ) : (
        <>
          <FiLock size={12} className="me-1" />
          Inactive
        </>
      )}
    </Badge>
  );
};

export default UsersPage;
